import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Sparkles, Landmark, Download, LogOut, ArrowUpRight, User as UserIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

export default function MyAccount() {
  const { user, loading, logout } = useAuth();
  const [reports, setReports] = useState({ ai_reports: [], funding_reports: [] });
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!user) return;
    api.get("/me/reports")
      .then((r) => setReports(r.data))
      .catch(() => {})
      .finally(() => setBusy(false));
  }, [user]);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;

  const total = reports.ai_reports.length + reports.funding_reports.length;

  return (
    <div className="min-h-screen bg-[var(--tmg-ivory)]">
      <header className="bg-[var(--tmg-obsidian)] text-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-16 flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <div>
            <p className="tmg-overline-gold mb-3">— Your Account</p>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold leading-tight">
              Welcome back{user.name ? `, ${user.name.split(" ")[0]}` : ""}.
            </h1>
            <p className="mt-4 text-white/70 flex items-center gap-3 text-sm">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
              ) : (
                <UserIcon className="w-4 h-4" />
              )}
              {user.email}
            </p>
          </div>
          <button onClick={logout} data-testid="user-logout-btn" className="text-xs text-white/70 hover:text-[var(--tmg-gold)] uppercase tracking-[0.14em] flex items-center gap-2">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-10 py-14">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="tmg-overline mb-1">— Saved Reports</p>
            <h2 className="font-display text-2xl font-semibold">
              {total} report{total === 1 ? "" : "s"} on file
            </h2>
          </div>
          <div className="flex gap-3">
            <Link to="/ai-readiness" className="tmg-btn-secondary text-xs px-5 py-3">
              <Sparkles className="w-3.5 h-3.5" /> Take AI Readiness
            </Link>
            <Link to="/funding-readiness-worksheet" className="tmg-btn-secondary text-xs px-5 py-3">
              <Landmark className="w-3.5 h-3.5" /> Take Funding
            </Link>
          </div>
        </div>

        {busy ? (
          <p className="text-[var(--tmg-muted)]">Loading your reports…</p>
        ) : total === 0 ? (
          <div className="bg-white border border-[var(--tmg-line)] p-12 text-center">
            <p className="font-display text-xl mb-2">No reports yet.</p>
            <p className="text-sm text-[var(--tmg-muted)] mb-6">Take a worksheet and it'll be saved here automatically.</p>
            <div className="flex justify-center gap-3">
              <Link to="/ai-readiness" className="tmg-btn-primary text-xs">
                <Sparkles className="w-3.5 h-3.5" /> AI Readiness
              </Link>
              <Link to="/funding-readiness-worksheet" className="tmg-btn-primary text-xs">
                <Landmark className="w-3.5 h-3.5" /> Funding
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {reports.ai_reports.length > 0 && (
              <ReportGroup title="AI Readiness Reports" icon={Sparkles} items={reports.ai_reports} endpoint="leads" />
            )}
            {reports.funding_reports.length > 0 && (
              <ReportGroup title="Funding Readiness Reports" icon={Landmark} items={reports.funding_reports} endpoint="funding-leads" />
            )}
          </div>
        )}

        <div className="mt-16 bg-[var(--tmg-obsidian)] text-white p-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div>
            <p className="tmg-overline-gold mb-2">— Ready for the Real Thing?</p>
            <h3 className="font-display text-2xl font-semibold leading-tight">
              Turn your report into a strategy call.
            </h3>
          </div>
          <Link to="/book-call" className="tmg-btn-primary">
            Book a Discovery Call <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </main>
    </div>
  );
}

function ReportGroup({ title, icon: Icon, items, endpoint }) {
  return (
    <section>
      <div className="flex items-center gap-3 mb-4">
        <Icon className="w-5 h-5 text-[var(--tmg-gold)]" />
        <h3 className="font-display text-xl font-semibold">{title}</h3>
        <span className="text-xs text-[var(--tmg-muted)] font-mono-sm">{items.length}</span>
      </div>
      <div className="divide-y divide-[var(--tmg-line)] bg-white border border-[var(--tmg-line)]">
        {items.map((it) => (
          <div key={it.id} data-testid={`my-report-${it.id}`} className="flex items-center gap-4 p-5">
            <div className="w-14 h-14 bg-[var(--tmg-obsidian)] text-[var(--tmg-gold)] flex-shrink-0 flex items-center justify-center">
              <div className="text-center">
                <div className="font-display text-lg font-semibold leading-none">{it.score_percent}%</div>
                <div className="text-[8px] font-mono-sm uppercase mt-0.5">score</div>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1 flex-wrap">
                <p className="font-semibold">{it.score_tier}</p>
                <Badge variant="outline" className="border-[var(--tmg-gold)] text-[var(--tmg-gold-soft)]">
                  {it.recommendation}
                </Badge>
              </div>
              <p className="text-xs text-[var(--tmg-muted)] font-mono-sm">
                {new Date(it.created_at).toLocaleString()}
              </p>
            </div>
            <a
              href={`${API}/${endpoint}/${it.id}/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="tmg-btn-secondary text-xs px-4 py-2"
            >
              <Download className="w-3.5 h-3.5" /> PDF
            </a>
          </div>
        ))}
      </div>
    </section>
  );
}
